import { Component, OnInit, Input } from '@angular/core';
import { ValueAccessorBase } from '../../shared/value-accessor/value-accessor-base';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { RequestModel } from 'src/app/models/request/request-model';

/**
 * Dashboard "AI asistent" card.
 *
 * settings.ApiEndpoint (module, POST) returns { Message, Buttons?, Facts? }:
 *   - Message is a deterministic text computed by the module; it is shown when the AI is off
 *     (settings.UseAi === false), when the endpoint returns no Facts, or when the AI call fails;
 *   - when Facts are present and settings.UseAi !== false, the card keeps its loading text and
 *     shows the Core `api/ai/insights` summary once it arrives (single message, no swap). The module-specific
 *     system instructions come from settings.AiInstructions (filled in the platform editor);
 *     Core itself is module-agnostic and only adds output-format rules.
 * Without an endpoint the card shows the placeholder text (design preview only).
 */
@Component({
    selector: 'sem-ai-assistant',
    templateUrl: './sem-ai-assistant.component.html',
    styleUrls: ['./sem-ai-assistant.component.css'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: SemAIAssistantComponent, multi: true }
    ],
    standalone: false
})
export class SemAIAssistantComponent extends ValueAccessorBase<string> implements OnInit {

  @Input() settings: any;

  /** Design-time preview only (no endpoint configured). Application wording comes from the module. */
  static readonly PLACEHOLDER_MESSAGE = 'Configure <strong>ApiEndpoint</strong> in the control settings to show a live summary here.';
  static readonly PLACEHOLDER_BUTTONS = [
    { Label: 'Button 1', Url: '#', Icon: 'chart' },
    { Label: 'Button 2', Url: '#', Icon: 'check' }
  ];

  message: string = '';
  buttons: any[] = [];
  loading = false;
  /** 'placeholder' | 'module' | 'ai' - which text is currently displayed */
  source = 'placeholder';

  ngOnInit() {
    const endpoint: string = this.settings?.ApiEndpoint;
    const configuredButtons = this.readButtons(this.settings?.Buttons);

    if (!endpoint) {
      this.message = SemAIAssistantComponent.PLACEHOLDER_MESSAGE;
      this.buttons = configuredButtons.length ? configuredButtons : SemAIAssistantComponent.PLACEHOLDER_BUTTONS;
      return;
    }

    this.loading = true;
    this.message = this.settings?.LoadingText || '…';
    this.buttons = configuredButtons;

    const req = new RequestModel();
    req.url = endpoint;
    req.data = {};

    this.restProviderService.getDataPOST<any>(req).subscribe({
      next: (res) => {
        const payload = typeof res === 'string' ? this.tryParse(res) : res;
        const fallback: string = payload?.Message ?? payload?.message ?? (typeof res === 'string' ? res : '');
        const responseButtons = this.readButtons(payload?.Buttons ?? payload?.buttons);
        if (responseButtons.length) {
          this.buttons = responseButtons;
        }

        const facts = payload?.Facts ?? payload?.facts;
        if (facts && this.settings?.UseAi !== false) {
          // keep the loading text until the AI answers; the module text is used only if the AI fails
          this.requestAiSummary(facts, fallback);
        } else {
          this.showModuleText(fallback);
        }
      },
      error: () => {
        // keep the card useful even when the module endpoint fails
        this.message = this.settings?.ErrorText || '';
        this.loading = false;
      }
    });
  }

  private requestAiSummary(facts: any, fallback: string) {
    const req = new RequestModel();
    req.url = 'api/ai/insights';
    req.data = {
      Facts: facts,
      FallbackMessage: fallback,
      Language: this.localStorageLanguage(),
      Instructions: this.settings?.AiInstructions || ''
    };

    this.restProviderService.getDataPOST<any>(req).subscribe({
      next: (res) => {
        const payload = typeof res === 'string' ? this.tryParse(res) : res;
        const text: string = payload?.Message ?? payload?.message;
        if (text && (payload?.Source ?? payload?.source) === 'ai') {
          this.message = text;
          this.source = 'ai';
          this.loading = false;
        } else {
          // AI disabled, empty or unreachable on the server side -> deterministic module text
          this.showModuleText(fallback);
        }
      },
      error: () => { this.showModuleText(fallback); }
    });
  }

  private showModuleText(text: string) {
    this.message = text;
    this.source = 'module';
    this.loading = false;
  }

  private readButtons(raw: any): any[] {
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw : (raw.$values ?? []);
    return list.filter((b: any) => b && b.Label);
  }

  private tryParse(text: string): any {
    try { return JSON.parse(text); } catch { return null; }
  }

  private localStorageLanguage(): string {
    try { return localStorage.getItem('language') || 'sl'; } catch { return 'sl'; }
  }

  navigate(url: string) {
    if (!url || url === '#') return;
    this.utility.processNavigationUrl(url);
  }

}
